import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createBrowserRouter } from 'react-router';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Toaster } from '@/components/Toaster';
import { DraftNavigationGuard } from '@/components/workspace/DraftNavigationGuard';
import { bindExerciseToSessionIdentity } from '@/features/training/exerciseStore';
import { bindTourToSessionIdentity } from '@/features/training/tourStore';
import { WorkspaceDataSourceProvider } from '@/features/workspace/WorkspaceDataSourceProvider';
import { createQueryClient } from '@/lib/queryClient';
import { bindCacheToSessionIdentity } from '@/lib/workspaceQueryKeys';
import { applyInitialLanguage } from '@/stores/language';
import { bindDraftsToSessionIdentity } from '@/stores/workspaceDrafts';
import { captureException } from '@/utils/sentry';
import { AppRoutes } from './router/AppRoutes';
import { RootRouteError } from './router/RouteErrorScreen';
import { SessionGate } from './router/SessionGate';
import './utils/i18n';

applyInitialLanguage();

const queryClient = createQueryClient();

/* Данные и черновики прежнего пользователя не переживают выход и вход другого пользователя */
bindCacheToSessionIdentity(queryClient);
bindDraftsToSessionIdentity();
bindTourToSessionIdentity();
bindExerciseToSessionIdentity();

/**
 * Корень маршрутизации. Data router нужен защите незавершённых изменений (useBlocker);
 * сами маршруты по-прежнему описывает AppRoutes.
 */
function RouterRoot() {
	return (
		<>
			<SessionGate>
				<WorkspaceDataSourceProvider>
					<AppRoutes />
				</WorkspaceDataSourceProvider>
			</SessionGate>
			<DraftNavigationGuard />
			{/* Глобальные toast-уведомления */}
			<Toaster />
		</>
	);
}

/**
 * Падение страницы в каркасе ловит сам каркас (AppLayout); errorElement корня -- всё
 * остальное: гостевые страницы и сам каркас. Без него маршрутизатор показал бы свою
 * заглушку для разработчика.
 */
const router = createBrowserRouter([
	{ path: '*', element: <RouterRoot />, errorElement: <RootRouteError /> },
]);

/** Падения, пойманные маршрутизатором, уходят в отчёт об ошибках, как и пойманные ErrorBoundary */
function reportRouteError(error: unknown) {
	captureException(error);
	console.error('Route render failed:', error);
}

export function App() {
	return (
		<ErrorBoundary>
			<QueryClientProvider client={queryClient}>
				<RouterProvider router={router} onError={reportRouteError} />
			</QueryClientProvider>
		</ErrorBoundary>
	);
}
